import { supabase } from './client';
import type { RealtimeChannel } from '@supabase/supabase-js';

// ============================================================
// REALTIME SUBSCRIPTIONS
// ============================================================
// NOTE: postgres_changes subscriptions are disabled because they require
// database replication to be enabled in Supabase Dashboard.
// The application uses BROADCAST channels and POLLING instead.
// ============================================================

export type SlideChangePayload = {
  session_id: string;
  current_slide: number;
};

export type SubmissionPayload = {
  id: string;
  session_id: string;
  slide_id: string;
  student_name: string;
  answer: Record<string, any>;
  created_at: string;
};

export type ParticipantPayload = {
  id: string;
  session_id: string;
  student_name: string;
  joined_at: string;
};

export type GradeUpdatePayload = {
  id: string;
  session_id: string;
  student_name: string;
  total_score: number;
  completion_percentage: number;
};

// ============================================================
// SESSION CHANNEL SUBSCRIPTION (Using Broadcast Only)
// ============================================================

/**
 * Subscribe to session broadcast events
 * This uses BROADCAST channels only (no postgres_changes)
 * 
 * For data changes, the app uses polling as a fallback
 */
export function subscribeToSession(
  sessionId: string,
  callbacks: {
    onSlideChange?: (payload: SlideChangePayload) => void;
    onNewParticipant?: (payload: ParticipantPayload) => void;
    onNewSubmission?: (payload: SubmissionPayload) => void;
    onGradeUpdate?: (payload: GradeUpdatePayload) => void;
    onSessionEnd?: (payload: { session_id: string }) => void;
  }
): RealtimeChannel {
  const channel = supabase.channel(`session_${sessionId}`, {
    config: {
      broadcast: { self: false },
      presence: { key: '' }
    }
  });

  // Listen to broadcast events only (not postgres_changes)
  // Teachers will broadcast events when they make changes

  if (callbacks.onSlideChange) {
    channel.on('broadcast', { event: 'slide_change' }, ({ payload }) => {
      callbacks.onSlideChange?.(payload);
    });
  }

  if (callbacks.onNewParticipant) {
    channel.on('broadcast', { event: 'new_participant' }, ({ payload }) => {
      callbacks.onNewParticipant?.(payload);
    });
  }

  if (callbacks.onNewSubmission) {
    channel.on('broadcast', { event: 'new_submission' }, ({ payload }) => {
      callbacks.onNewSubmission?.(payload);
    });
  }

  if (callbacks.onGradeUpdate) {
    channel.on('broadcast', { event: 'grade_update' }, ({ payload }) => {
      callbacks.onGradeUpdate?.(payload);
    });
  }

  if (callbacks.onSessionEnd) {
    channel.on('broadcast', { event: 'session_ended' }, ({ payload }) => {
      callbacks.onSessionEnd?.(payload);
    });
  }

  // Subscribe to the channel
  channel.subscribe((status, err) => {
    if (status === 'SUBSCRIBED') {
      console.log(`✅ Subscribed to session ${sessionId} (broadcast mode)`);
    } else if (status === 'CHANNEL_ERROR') {
      console.error(`❌ Error subscribing to session ${sessionId}`, err);
      console.warn('⚠️ Broadcast subscription failed. Falling back to polling.');
    } else if (status === 'TIMED_OUT') {
      console.error(`⏱️ Subscription timed out for session ${sessionId}`);
      console.warn('⚠️ Falling back to polling.');
    } else {
      console.log(`📡 Subscription status for session ${sessionId}:`, status);
    }
  });

  return channel;
}

/**
 * Unsubscribe from session channel
 */
export async function unsubscribeFromSession(channel: RealtimeChannel) {
  await supabase.removeChannel(channel);
}

// ============================================================
// BROADCAST FUNCTIONS (Teacher Only)
// ============================================================

/**
 * Broadcast slide change to all students in session
 * Use this in addition to database update for instant UI feedback
 */
export async function broadcastSlideChange(
  channel: RealtimeChannel,
  slideNumber: number
) {
  console.log('📢 Broadcasting slide change:', slideNumber);
  await channel.send({
    type: 'broadcast',
    event: 'slide_change',
    payload: { 
      current_slide: slideNumber,
      session_id: channel.topic.replace('session_', '')
    }
  });
}

/**
 * Broadcast session end to all students in session
 * Students will be notified and redirected to join another session
 */
export async function broadcastSessionEnd(
  channel: RealtimeChannel,
  sessionId: string
) {
  console.log('📢 Broadcasting session end');
  await channel.send({
    type: 'broadcast',
    event: 'session_ended',
    payload: {
      session_id: sessionId
    }
  });
}

/**
 * Broadcast custom message to session.
 * Uses httpSend() (REST delivery) to avoid the deprecated send() REST fallback
 * that fires when the channel isn't in a joined state.
 */
export async function broadcastToSession(
  channel: RealtimeChannel,
  event: string,
  payload: any
) {
  await channel.httpSend(event, payload);
}

// ============================================================
// PRESENCE TRACKING (Optional - for "who's online")
// ============================================================

export type PresenceState = {
  user_name: string;
  user_type: 'teacher' | 'student';
  online_at: string;
};

/**
 * Track user presence in session
 */
export function trackPresence(
  channel: RealtimeChannel,
  userName: string,
  userType: 'teacher' | 'student'
) {
  channel.track({
    user_name: userName,
    user_type: userType,
    online_at: new Date().toISOString()
  });

  // Listen to presence changes
  channel.on('presence', { event: 'sync' }, () => {
    const state = channel.presenceState();
    console.log('Online users:', state);
  });

  channel.on('presence', { event: 'join' }, ({ key, newPresences }) => {
    console.log('User joined:', newPresences);
  });

  channel.on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
    console.log('User left:', leftPresences);
  });
}

// ============================================================
// STUDENT-SPECIFIC REALTIME (for Student-Paced Mode)
// ============================================================

/**
 * Subscribe to student's own grade updates via broadcast
 * Teachers should broadcast grade updates when grading happens
 */
export function subscribeToStudentGrade(
  sessionId: string,
  studentName: string,
  onGradeUpdate: (payload: GradeUpdatePayload) => void
): RealtimeChannel {
  const channel = supabase.channel(`student_grade_${sessionId}_${studentName}`, {
    config: {
      broadcast: { self: false }
    }
  });

  // Listen for broadcast grade updates
  channel.on('broadcast', { event: 'grade_update' }, ({ payload }) => {
    if (payload.student_name === studentName) {
      onGradeUpdate(payload);
    }
  });

  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      console.log(`✅ Subscribed to grade updates for ${studentName}`);
    }
  });
  
  return channel;
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Get all active channels
 */
export function getActiveChannels() {
  return supabase.getChannels();
}

/**
 * Cleanup all subscriptions
 */
export async function cleanupAllSubscriptions() {
  const channels = getActiveChannels();
  await Promise.all(channels.map(channel => supabase.removeChannel(channel)));
  console.log('✅ All realtime subscriptions cleaned up');
}